import { ThemeEvents, VariantUpdateEvent } from '@theme/events';
import { Component } from '@theme/component';

/**
 * @typedef {Object} ProductPriceRefs
 * @property {HTMLElement} priceContainer
 * @property {HTMLElement} [volumePricingNote]
 */

/**
 * A custom element that displays a product price.
 * This component listens for variant update events and updates the price display accordingly.
 * It handles price updates from two different sources:
 * 1. Variant picker (in quick add modal or product page)
 * 2. Swatches variant picker (in product cards)
 *
 * @extends {Component<ProductPriceRefs>}
 */
class ProductPrice extends Component {
  #wpdObserver = null;

  connectedCallback() {
    super.connectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.addEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    this.#wpdObserver = new MutationObserver(() => this.#wrapWPDPriceTextNodes());
    this.#wpdObserver.observe(this, { subtree: true, childList: true });
    // Run immediately in case WPD already injected before this element connected
    this.#wrapWPDPriceTextNodes();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    const closestSection = this.closest('.shopify-section, dialog');
    if (!closestSection) return;
    closestSection.removeEventListener(ThemeEvents.variantUpdate, this.updatePrice);
    this.#wpdObserver?.disconnect();
  }

  #wrapWPDPriceTextNodes() {
    const compareAtElements = this.querySelectorAll('.wpd-ext-compare-at-price');
    for (const compareEl of compareAtElements) {
      const parent = compareEl.parentElement;
      if (!parent) continue;
      for (const node of [...parent.childNodes]) {
        if (node.nodeType === Node.TEXT_NODE && node.textContent.trim() !== '') {
          const span = document.createElement('span');
          span.className = 'wpd-actual-price';
          node.replaceWith(span);
          span.appendChild(node);
        }
      }
    }
    const actualPrice = this.querySelector('.wpd-actual-price');
    if (!actualPrice) return;
    const price = actualPrice.textContent.trim();
    if (!price) return;
    const target = document.querySelector('.accordeon--price.get');
    if (target) target.dataset.test = price;
  }

  /**
   * Updates the price and volume pricing note.
   * @param {VariantUpdateEvent} event - The variant update event.
   */
  updatePrice = (event) => {
    if (event.detail.data.newProduct) {
      this.dataset.productId = event.detail.data.newProduct.id;
    } else if (event.target instanceof HTMLElement && event.target.dataset.productId !== this.dataset.productId) {
      return;
    }

    const { priceContainer, volumePricingNote } = this.refs;
    // Find the new product-price element in the updated HTML
    const newProductPrice = event.detail.data.html.querySelector(
      `product-price[data-block-id="${this.dataset.blockId}"]`
    );
    if (!newProductPrice) return;

    // Update price container
    const newPrice = newProductPrice.querySelector('[ref="priceContainer"]');
    if (newPrice && priceContainer) {
      priceContainer.replaceWith(newPrice);
    }

    // Update volume pricing note
    const newNote = newProductPrice.querySelector('[ref="volumePricingNote"]');

    if (!newNote) {
      volumePricingNote?.remove();
    } else if (!volumePricingNote) {
      // Use newPrice since priceContainer was just replaced and now points to the detached element
      newPrice?.insertAdjacentElement('afterend', /** @type {Element} */ (newNote.cloneNode(true)));
    } else {
      volumePricingNote.replaceWith(newNote);
    }
  };
}

if (!customElements.get('product-price')) {
  customElements.define('product-price', ProductPrice);
}
